import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button, Card } from '@medikei/ui';
import { authApi, AuthApiError } from '@/api/http-client';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type VerifyStatus = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerifyStatus>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Token de verificação não fornecido.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await authApi.verifyEmail(token);
        setStatus('success');
        setMessage(response.message || 'Email verificado com sucesso!');
      } catch (err) {
        setStatus('error');
        const error = err as Error;
        if (error instanceof AuthApiError) {
          if (error.code === 'AUTH_INVALID_VERIFICATION_TOKEN') {
            setMessage('Token de verificação inválido ou expirado.');
          } else {
            setMessage(error.message);
          }
        } else {
          setMessage('Erro ao verificar email. Tente novamente.');
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-background p-4">
      <Card className="p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <Loader2 size={32} className="text-primary-500 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Verificando Email...
            </h2>
            <p className="text-muted-foreground">
              Por favor, aguarde enquanto verificamos o seu email.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Email Verificado!
            </h2>
            <p className="text-muted-foreground mb-6">
              {message}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Agora você pode fazer login na sua conta.
            </p>
            <Link to="/login">
              <Button variant="primary" className="w-full">
                Fazer Login
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Erro na Verificação
            </h2>
            <p className="text-muted-foreground mb-6">
              {message}
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Voltar para Login
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="ghost" className="w-full">
                  Criar nova conta
                </Button>
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
